/**
 * Plain-TypeScript shapes for the LLM model registry. The registry is static,
 * in-repo data, so model shapes are expressed as TypeScript types and defaults
 * are applied in plain code (`models.ts`) rather than through a validation
 * library. Its one runtime dependency is `moment`, used by `lifecycle.ts` for
 * UTC-correct plain-date math (the repo's date convention). See the package
 * README for the longer-term plan (single source of truth + generated JSON
 * Schema for non-TypeScript consumers).
 */
export var LlmProvider;
(function (LlmProvider) {
    LlmProvider["OpenAI"] = "openai";
    LlmProvider["Anthropic"] = "anthropic";
    LlmProvider["Google"] = "google";
    LlmProvider["Meta"] = "meta";
    LlmProvider["Amazon"] = "amazon";
    LlmProvider["DeepSeek"] = "deepseek";
})(LlmProvider || (LlmProvider = {}));
/** Content types a model can accept (input) or produce (output). */
export var LlmModelModality;
(function (LlmModelModality) {
    LlmModelModality["Text"] = "Text";
    LlmModelModality["File"] = "File";
    LlmModelModality["Image"] = "Image";
})(LlmModelModality || (LlmModelModality = {}));
/**
 * Stable model identifiers. The string VALUES are persisted in saved Flow
 * Builder block configs (`config.modelName`), so they MUST NOT change — only
 * their home moves into this package.
 *
 * The member KEYS intentionally keep `snake_case` rather than the usual
 * `camelCase`, for three reasons: they are migrated verbatim from the former
 * `web/vivaa-api` `llmPrompt/config.ts` enum (so no consumer has to churn); they
 * mirror the equivalent model enums elsewhere in the system (`backend/flow/api`,
 * `backend/predict/documents`), keeping the names aligned across the codebase as
 * this package grows toward the single source of truth; and they encode dotted
 * version numbers (e.g. `gpt_5_4`, `gemini_2_5_pro`) where the underscore is the
 * only separator that keeps `2_5` (2.5) from collapsing into an ambiguous `25` —
 * camelCasing would defeat the clarity the naming convention exists to protect.
 */
export var LlmModelName;
(function (LlmModelName) {
    LlmModelName["amazon_nova_pro"] = "amazon.nova-pro-v1:0";
    LlmModelName["claude_3_5_haiku"] = "anthropic.claude-3-5-haiku-20241022-v1:0";
    LlmModelName["claude_3_5_sonnet_v2"] = "anthropic.claude-3-5-sonnet-20241022-v2:0";
    LlmModelName["claude_3_7_sonnet_v1"] = "anthropic.claude-3-7-sonnet-20250219-v1:0";
    LlmModelName["claude_opus_4"] = "anthropic.claude-opus-4";
    LlmModelName["claude_sonnet_4"] = "anthropic.claude-sonnet-4";
    LlmModelName["deepseek_r1"] = "deepseek-r1";
    LlmModelName["deepseek_v3"] = "deepseek-ai/DeepSeek-V3";
    LlmModelName["gemini_1_5_pro"] = "gemini-1.5-pro-002";
    LlmModelName["gemini_1_5_flash"] = "gemini-1.5-flash-002";
    LlmModelName["gemini_2_0_flash_001"] = "gemini-2.0-flash-001";
    LlmModelName["gemini_2_0_flash_lite"] = "gemini-2.0-flash-lite";
    LlmModelName["gemini_2_0_flash_thinking_exp_01_21"] = "gemini-2.0-flash-thinking-exp-01-21";
    LlmModelName["gemini_2_5_flash"] = "gemini-2.5-flash";
    LlmModelName["gemini_2_5_flash_lite"] = "gemini-2.5-flash-lite";
    LlmModelName["gemini_2_5_pro_preview_03_25"] = "gemini-2.5-pro-preview-03-25";
    LlmModelName["gemini_2_5_pro"] = "gemini-2.5-pro";
    LlmModelName["gemini_3_pro_preview"] = "gemini-3-pro-preview";
    LlmModelName["gemini_3_flash_preview"] = "gemini-3-flash-preview";
    LlmModelName["gemini_3_1_pro_preview"] = "gemini-3.1-pro-preview";
    LlmModelName["gemini_3_1_flash_lite_preview"] = "gemini-3.1-flash-lite-preview";
    LlmModelName["gpt_4_1"] = "gpt-4.1";
    LlmModelName["gpt_4_1_mini"] = "gpt-4.1-mini";
    LlmModelName["gpt_4_1_nano"] = "gpt-4.1-nano";
    LlmModelName["gpt_4o"] = "gpt-4o";
    LlmModelName["gpt_4o_mini"] = "gpt-4o-mini";
    LlmModelName["gpt_5"] = "gpt-5";
    LlmModelName["gpt_5_mini"] = "gpt-5-mini";
    LlmModelName["gpt_5_nano"] = "gpt-5-nano";
    LlmModelName["gpt_5_1"] = "gpt-5.1";
    LlmModelName["gpt_5_2"] = "gpt-5.2";
    LlmModelName["gpt_5_4"] = "gpt-5.4";
    LlmModelName["gpt_5_4_mini"] = "gpt-5.4-mini";
    LlmModelName["gpt_5_4_nano"] = "gpt-5.4-nano";
    LlmModelName["gpt_5_6_sol"] = "gpt-5.6-sol";
    LlmModelName["gpt_5_6_terra"] = "gpt-5.6-terra";
    LlmModelName["gpt_5_6_luna"] = "gpt-5.6-luna";
    LlmModelName["claude_opus_4_1"] = "anthropic.claude-opus-4-1-20250805-v1:0";
    LlmModelName["claude_opus_4_5"] = "anthropic.claude-opus-4-5-20251015-v1:0";
    LlmModelName["claude_haiku_4_5"] = "anthropic.claude-haiku-4-5-20251015-v1:0";
    LlmModelName["claude_sonnet_4_5"] = "anthropic.claude-sonnet-4-5-20250929-v1:0";
    LlmModelName["claude_opus_4_6"] = "anthropic.claude-opus-4-6-v1";
    LlmModelName["claude_sonnet_4_6"] = "anthropic.claude-sonnet-4-6";
    LlmModelName["claude_opus_5_5"] = "anthropic.claude-opus-5-5";
    LlmModelName["meta_llama_3_2_11b"] = "meta.llama3-2-11b-instruct-v1:0";
    LlmModelName["meta_llama_3_3_70b"] = "meta.llama3-3-70b-instruct-v1:0";
    LlmModelName["o1"] = "o1";
    LlmModelName["o3"] = "o3";
    LlmModelName["o1_mini"] = "o1-mini";
    LlmModelName["o3_mini"] = "o3-mini";
    LlmModelName["o4_mini"] = "o4-mini";
    LlmModelName["notable_default"] = "notable-default";
})(LlmModelName || (LlmModelName = {}));
/** Lifecycle state, derived at runtime from a model's added/retired/obsolete dates. */
export var LlmModelLifecycleState;
(function (LlmModelLifecycleState) {
    /** Scheduled: `addedDate` is still in the future, so the model is neither offered nor run. */
    LlmModelLifecycleState["Upcoming"] = "Upcoming";
    LlmModelLifecycleState["Active"] = "Active";
    LlmModelLifecycleState["Retired"] = "Retired";
    LlmModelLifecycleState["Obsolete"] = "Obsolete";
})(LlmModelLifecycleState || (LlmModelLifecycleState = {}));
/**
 * Model family aliases (mirrors flow-api `MODEL_FAMILIES`). A model that is
 * obsolete or upcoming runs its successor family's first executable candidate.
 */
export var LlmModelFamily;
(function (LlmModelFamily) {
    LlmModelFamily["GeminiPro"] = "geminiPro";
    LlmModelFamily["GeminiFlash"] = "geminiFlash";
    LlmModelFamily["GeminiFlashLite"] = "geminiFlashLite";
    LlmModelFamily["ClaudeOpus"] = "claudeOpus";
    LlmModelFamily["ClaudeSonnet"] = "claudeSonnet";
    LlmModelFamily["ClaudeHaiku"] = "claudeHaiku";
    LlmModelFamily["Gpt"] = "gpt";
    LlmModelFamily["GptMini"] = "gptMini";
    LlmModelFamily["GptNano"] = "gptNano";
    LlmModelFamily["O"] = "o";
    LlmModelFamily["OMini"] = "oMini";
    LlmModelFamily["AmazonNova"] = "amazonNova";
    LlmModelFamily["DeepseekReasoning"] = "deepseekReasoning";
    LlmModelFamily["DeepseekChat"] = "deepseekChat";
    LlmModelFamily["MetaLlama"] = "metaLlama";
    LlmModelFamily["NotableDefault"] = "notableDefault";
})(LlmModelFamily || (LlmModelFamily = {}));
/** Whether a model supports a given API parameter. */
export var LlmParameterSupport;
(function (LlmParameterSupport) {
    LlmParameterSupport["Supported"] = "supported";
    LlmParameterSupport["Unsupported"] = "unsupported";
})(LlmParameterSupport || (LlmParameterSupport = {}));
/**
 * Temperature support. Some models accept `temperature` and `top_p`
 * individually but not together; `ExcludesTopP` means temperature is supported
 * but must not be combined with top_p.
 */
export var LlmTemperatureSupport;
(function (LlmTemperatureSupport) {
    LlmTemperatureSupport["Supported"] = "supported";
    LlmTemperatureSupport["Unsupported"] = "unsupported";
    LlmTemperatureSupport["ExcludesTopP"] = "excludesTopP";
})(LlmTemperatureSupport || (LlmTemperatureSupport = {}));
/** Top-p support; the mirror of {@link LlmTemperatureSupport} for `top_p`. */
export var LlmTopPSupport;
(function (LlmTopPSupport) {
    LlmTopPSupport["Supported"] = "supported";
    LlmTopPSupport["Unsupported"] = "unsupported";
    LlmTopPSupport["ExcludesTemperature"] = "excludesTemperature";
})(LlmTopPSupport || (LlmTopPSupport = {}));
//# sourceMappingURL=types.js.map